import { PlusOutlined, ReloadOutlined, RightOutlined } from '@ant-design/icons';
import {
  Alert,
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Table,
  Tooltip,
  Typography,
  message,
} from 'antd';
import type { TableColumnsType } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import { listCountries } from '../api/countries';
import type { Country } from '../api/countries';
import { CustomerLevel, createCustomer, listCustomers } from '../api/customers';
import type { CustomerListItem } from '../api/customers';
import { ApiError } from '../api/errors';
import { PermissionCodes } from '../auth/permissions';
import { useAuth } from '../auth/useAuth';
import { AppRoutes } from '../routes';
import {
  indentFor,
  levelOptions,
  levelTag,
  statusOptions,
  statusTag,
} from './customers/customerDisplay';

/** Giá trị của biểu mẫu khai khách hàng mới. */
interface CustomerFormValues {
  readonly level: number;
  readonly code: string;
  readonly legalName: string;
  readonly shortName: string;
  readonly nameEn: string | null;
  readonly taxCode: string | null;
  readonly countryId: string;
  readonly invoiceAddress: string | null;
  readonly parentId: string | null;
  readonly sortOrder: number;
}

/** Dữ liệu của một trang đã nạp xong, kèm khóa nhận dạng lần nạp đó. */
interface LoadedPage {
  readonly key: string;
  readonly items: readonly CustomerListItem[];
  readonly totalCount: number;
}

/** Số bản ghi mỗi trang. Khớp với mặc định của máy chủ. */
const pageSize = 25;

/**
 * Số bản ghi nạp cho ô chọn nút cha và ô chọn quốc gia.
 *
 * Bằng giới hạn lớn nhất của máy chủ. Cây khách hàng của một công ty tích hợp hệ thống thị giác có
 * hàng chục nút, không phải hàng nghìn, nên một lần nạp là đủ. Khi vượt ngưỡng này thì ô chọn phải
 * đổi sang tìm kiếm theo từ khóa — và lúc đó nó sẽ hiện thiếu nút cha một cách âm thầm, nên chỗ này
 * cần được xem lại trước khi cây lớn tới mức đó.
 */
const lookupPageSize = 200;

/**
 * Trang danh sách khách hàng.
 *
 * Ba điểm khác các trang danh mục đã dựng, và cả ba đều xuất phát từ chính nghiệp vụ:
 *
 * 1. **Bảng phẳng nhưng thụt lề theo cây.** Máy chủ sắp theo đường dẫn nên mọi nút con nằm ngay
 *    dưới nút cha của nó; thụt lề là thứ duy nhất còn thiếu để bảng đọc được như một cây. Không
 *    dựng cây thật ở phía giao diện, vì danh sách được phân trang — một nút cha ở trang này có thể
 *    có con ở trang sau, và cây dựng từ một trang sẽ thiếu nhánh mà không có dấu hiệu gì.
 *
 * 2. **Không sửa tại chỗ trên bảng.** Mỗi nhóm thông tin của khách hàng có một quyền riêng, nên
 *    việc sửa nằm ở trang chi tiết, nơi từng nhóm hiện ra hoặc bị ẩn theo quyền thật.
 *
 * 3. **Trạng thái được tô màu.** Ba trạng thái chặn giao dịch phải nhìn ra ngay từ danh sách, trước
 *    khi người dùng bắt đầu gõ một báo giá không lập được.
 */
export function CustomerPage(): ReactNode {
  const { hasPermission } = useAuth();
  const navigate = useNavigate();

  const canCreate = hasPermission(PermissionCodes.CustomerCreate);

  const [form] = Form.useForm<CustomerFormValues>();

  const [pageNumber, setPageNumber] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [levelFilter, setLevelFilter] = useState<number | null>(null);
  const [statusFilter, setStatusFilter] = useState<number | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  const [loaded, setLoaded] = useState<LoadedPage | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [countries, setCountries] = useState<readonly Country[]>([]);
  const [parentCandidates, setParentCandidates] = useState<readonly CustomerListItem[]>([]);

  // Cấp đang chọn trong biểu mẫu quyết định hai thứ: có bắt buộc mã số thuế hay không, và nút cha
  // có bắt buộc hay không. Theo dõi riêng thay vì đọc từ biểu mẫu, vì cả hai phải đổi ngay khi
  // người dùng chọn cấp chứ không đợi tới lúc bấm lưu.
  const [selectedLevel, setSelectedLevel] = useState<number>(CustomerLevel.LegalEntity);

  const requestKey = useMemo(
    () => JSON.stringify({ searchTerm, levelFilter, statusFilter, pageNumber, reloadToken }),
    [searchTerm, levelFilter, statusFilter, pageNumber, reloadToken],
  );

  const isLoading = loaded?.key !== requestKey && errorMessage === null;

  const reload = useCallback((): void => {
    setReloadToken((token) => token + 1);
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    void (async () => {
      try {
        // Chỉ đưa vào những khóa thật sự có giá trị. Cấu hình TypeScript của dự án phân biệt
        // "không có khóa" với "có khóa mang giá trị rỗng", nên không truyền thẳng undefined được.
        const page = await listCustomers({
          searchTerm,
          ...(levelFilter === null ? {} : { level: levelFilter }),
          ...(statusFilter === null ? {} : { status: statusFilter }),
          pageNumber,
          pageSize,
        });

        if (controller.signal.aborted) {
          return;
        }

        setErrorMessage(null);
        setLoaded({ key: requestKey, items: page.items, totalCount: page.totalCount });
      } catch (error) {
        if (controller.signal.aborted) {
          return;
        }

        setErrorMessage(
          error instanceof ApiError
            ? error.message
            : 'Không kết nối được tới máy chủ. Kiểm tra lại đường mạng.',
        );
      }
    })();

    return () => {
      controller.abort();
    };
  }, [requestKey, searchTerm, levelFilter, statusFilter, pageNumber]);

  const items = useMemo<readonly CustomerListItem[]>(() => loaded?.items ?? [], [loaded]);
  const totalCount = loaded?.totalCount ?? 0;

  /**
   * Nạp dữ liệu cho hai ô chọn của biểu mẫu khai mới.
   *
   * Nạp lúc mở biểu mẫu chứ không nạp lúc mở trang: phần lớn lần vào trang này là để tra cứu, không
   * phải để khai thêm, nên hai lời gọi kia sẽ là hai lời gọi vô ích ở phần lớn số lần.
   */
  async function loadFormLookupsAsync(): Promise<void> {
    try {
      const [countryPage, customerPage] = await Promise.all([
        listCountries({ pageSize: lookupPageSize }),
        listCustomers({ pageSize: lookupPageSize }),
      ]);

      setCountries(countryPage.items);
      setParentCandidates(customerPage.items);
    } catch (error) {
      void message.error(
        error instanceof ApiError
          ? error.message
          : 'Không nạp được danh mục quốc gia và danh sách nút cha.',
      );
    }
  }

  function openCreateForm(): void {
    setSelectedLevel(CustomerLevel.LegalEntity);

    form.setFieldsValue({
      level: CustomerLevel.LegalEntity,
      code: '',
      legalName: '',
      shortName: '',
      nameEn: null,
      taxCode: null,
      countryId: '',
      invoiceAddress: null,
      parentId: null,
      sortOrder: 0,
    });

    setIsFormOpen(true);
    void loadFormLookupsAsync();
  }

  async function handleSubmit(values: CustomerFormValues): Promise<void> {
    setIsSaving(true);

    try {
      const created = await createCustomer({
        level: values.level,
        code: values.code,
        legalName: values.legalName,
        shortName: values.shortName,
        nameEn: values.nameEn,
        taxCode: values.taxCode,
        countryId: values.countryId,
        invoiceAddress: values.invoiceAddress,
        parentId: values.parentId,
        sortOrder: values.sortOrder,
      });

      void message.success('Đã khai khách hàng mới.');
      setIsFormOpen(false);

      // Đưa thẳng sang trang chi tiết: khách hàng vừa khai còn thiếu điều khoản thương mại và người
      // phụ trách, và đó là hai thứ cần khai tiếp ngay chứ không phải để sau.
      void navigate(`${AppRoutes.Customer}/${created.id}`);
    } catch (error) {
      void message.error(
        error instanceof ApiError ? error.message : 'Không lưu được. Thử lại sau.',
      );
    } finally {
      setIsSaving(false);
    }
  }

  const requiresTaxCode = selectedLevel === CustomerLevel.LegalEntity;
  const requiresParent =
    selectedLevel === CustomerLevel.Site || selectedLevel === CustomerLevel.Line;
  const forbidsParent = selectedLevel === CustomerLevel.Group;

  const columns: TableColumnsType<CustomerListItem> = [
    {
      title: 'Mã',
      dataIndex: 'code',
      width: 200,
      render: (code: string, customer: CustomerListItem) => (
        <Typography.Text strong>
          <span style={{ whiteSpace: 'pre' }}>{indentFor(customer.depth)}</span>
          {code}
        </Typography.Text>
      ),
    },
    { title: 'Tên gọi tắt', dataIndex: 'shortName', width: 200 },
    {
      title: 'Tên pháp lý',
      dataIndex: 'legalName',
      render: (legalName: string) => (
        <Tooltip title={legalName}>
          <span>{legalName}</span>
        </Tooltip>
      ),
    },
    {
      title: 'Cấp',
      dataIndex: 'level',
      width: 150,
      render: (level: number) => levelTag(level),
    },
    {
      title: 'Mã số thuế',
      dataIndex: 'taxCode',
      width: 140,
      render: (taxCode: string | null) => taxCode ?? '—',
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      width: 150,
      render: (status: number) => statusTag(status),
    },
    {
      title: '',
      key: 'actions',
      width: 100,
      render: (_value: unknown, customer: CustomerListItem) => (
        <Button
          size="small"
          icon={<RightOutlined />}
          onClick={() => {
            void navigate(`${AppRoutes.Customer}/${customer.id}`);
          }}
        >
          Mở
        </Button>
      ),
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Typography.Title level={4} style={{ margin: 0 }}>
        Đối tác
      </Typography.Title>

      {errorMessage !== null && <Alert type="error" showIcon message={errorMessage} />}

      <Alert
        type="info"
        showIcon
        message="Cây bốn cấp: tập đoàn, pháp nhân, nhà máy, line"
        description={
          'Hóa đơn xuất cho cấp pháp nhân, hàng giao tới cấp nhà máy, hệ thống lắp ở cấp line. '
          + 'Không cần khai đủ bốn cấp: khách hàng nhỏ chỉ cần một pháp nhân đứng một mình. '
          + 'Danh sách được sắp theo cây nên nút con luôn nằm ngay dưới nút cha của nó.'
        }
      />

      <Space wrap>
        <Input.Search
          placeholder="Tìm theo mã, tên hoặc mã số thuế"
          allowClear
          style={{ width: 300 }}
          onSearch={(value) => {
            setPageNumber(1);
            setSearchTerm(value);
          }}
        />

        <Select<number | null>
          allowClear
          placeholder="Mọi cấp"
          style={{ width: 180 }}
          value={levelFilter}
          options={[...levelOptions]}
          onChange={(value) => {
            setPageNumber(1);
            setLevelFilter(value ?? null);
          }}
        />

        <Select<number | null>
          allowClear
          placeholder="Mọi trạng thái"
          style={{ width: 180 }}
          value={statusFilter}
          options={[...statusOptions]}
          onChange={(value) => {
            setPageNumber(1);
            setStatusFilter(value ?? null);
          }}
        />

        <Button icon={<ReloadOutlined />} onClick={reload}>
          Tải lại
        </Button>

        {canCreate && (
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreateForm}>
            Khai khách hàng
          </Button>
        )}
      </Space>

      <Table<CustomerListItem>
        rowKey="id"
        size="small"
        columns={columns}
        dataSource={[...items]}
        loading={isLoading}
        scroll={{ x: 'max-content' }}
        pagination={{
          current: pageNumber,
          pageSize,
          total: totalCount,
          showSizeChanger: false,
          showTotal: (total, range) =>
            `${range[0].toLocaleString('vi-VN')}–${range[1].toLocaleString('vi-VN')}`
            + ` trong ${total.toLocaleString('vi-VN')}`,
          onChange: setPageNumber,
        }}
      />

      <Modal
        title="Khai khách hàng mới"
        open={isFormOpen}
        confirmLoading={isSaving}
        width={640}
        onOk={() => {
          form.submit();
        }}
        onCancel={() => {
          setIsFormOpen(false);
        }}
        okText="Lưu"
        cancelText="Hủy"
        destroyOnHidden
      >
        <Form<CustomerFormValues>
          form={form}
          layout="vertical"
          requiredMark={false}
          disabled={isSaving}
          onFinish={(values) => {
            void handleSubmit(values);
          }}
        >
          <Form.Item
            name="level"
            label="Cấp trong cây"
            extra="Không đổi được sau khi khai: cấp quyết định chứng từ nào lập được cho nút này."
            rules={[{ required: true, message: 'Chưa chọn cấp.' }]}
          >
            <Select<number>
              options={[...levelOptions]}
              onChange={(value) => {
                setSelectedLevel(value);
              }}
            />
          </Form.Item>

          <Form.Item
            name="parentId"
            label="Nút cha"
            extra={
              forbidsParent
                ? 'Cấp tập đoàn là nút gốc nên không có nút cha.'
                : requiresParent
                  ? 'Bắt buộc: nhà máy phải thuộc một pháp nhân, line phải thuộc nhà máy hoặc pháp nhân.'
                  : 'Để trống nếu khách hàng chỉ có một pháp nhân, không có tập đoàn mẹ.'
            }
            rules={
              requiresParent ? [{ required: true, message: 'Chưa chọn nút cha.' }] : []
            }
          >
            <Select<string | null>
              allowClear
              disabled={forbidsParent}
              showSearch
              optionFilterProp="label"
              placeholder="Chọn nút cha"
              options={parentCandidates.map((candidate) => ({
                value: candidate.id,
                label: `${indentFor(candidate.depth)}${candidate.code} — ${candidate.shortName}`,
              }))}
            />
          </Form.Item>

          <Form.Item
            name="code"
            label="Mã khách hàng"
            extra="Chữ in hoa không dấu, chữ số, gạch nối. Ví dụ: KH-SDV, KH-SDV-BN, KH-SDV-BN-L3."
            rules={[{ required: true, message: 'Chưa nhập mã khách hàng.' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="legalName"
            label="Tên pháp lý đầy đủ"
            extra={
              requiresTaxCode
                ? 'ĐƯỢC IN TRÊN HÓA ĐƠN. Phải khớp từng chữ với giấy chứng nhận đăng ký doanh nghiệp.'
                : 'Tên đầy đủ của nút này.'
            }
            rules={[{ required: true, message: 'Chưa nhập tên pháp lý.' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="shortName"
            label="Tên gọi tắt"
            extra="Hiện trên danh sách và ô chọn. Tên pháp lý đầy đủ quá dài để hiển thị trên một dòng."
            rules={[{ required: true, message: 'Chưa nhập tên gọi tắt.' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item name="nameEn" label="Tên tiếng Anh">
            <Input />
          </Form.Item>

          <Form.Item
            name="countryId"
            label="Quốc gia"
            extra="Quyết định việc có phải tính thuế nhà thầu nước ngoài, và là xuất xứ trên giấy tờ hải quan."
            rules={[{ required: true, message: 'Chưa chọn quốc gia.' }]}
          >
            <Select<string>
              showSearch
              optionFilterProp="label"
              placeholder="Chọn quốc gia"
              options={countries.map((country) => ({
                value: country.id,
                label: `${country.name} (${country.code})`,
              }))}
            />
          </Form.Item>

          <Form.Item
            name="taxCode"
            label="Mã số thuế"
            extra={
              requiresTaxCode
                ? 'BẮT BUỘC ở cấp pháp nhân: đó là thông tin in trên hóa đơn. Mười chữ số, hoặc mười chữ số kèm ba số đơn vị phụ thuộc.'
                : 'Cấp này không phải người nộp thuế nên thường để trống.'
            }
            rules={
              requiresTaxCode
                ? [{ required: true, message: 'Cấp pháp nhân bắt buộc có mã số thuế.' }]
                : []
            }
          >
            <Input placeholder="0111193780" />
          </Form.Item>

          <Form.Item
            name="invoiceAddress"
            label="Địa chỉ hóa đơn"
            extra={
              requiresTaxCode
                ? 'BẮT BUỘC ở cấp pháp nhân: đó là thông tin in trên hóa đơn.'
                : 'Địa chỉ giao hàng khai riêng ở trang chi tiết.'
            }
            rules={
              requiresTaxCode
                ? [{ required: true, message: 'Cấp pháp nhân bắt buộc có địa chỉ hóa đơn.' }]
                : []
            }
          >
            <Input.TextArea rows={2} />
          </Form.Item>

          <Form.Item
            name="sortOrder"
            label="Thứ tự trình bày"
            extra="Số nhỏ nằm trên, trong phạm vi các nút cùng cha."
            rules={[{ required: true, message: 'Chưa nhập thứ tự trình bày.' }]}
          >
            <InputNumber style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
